import {
  ApiOutlined,
  DisconnectOutlined,
  MoreOutlined,
  ReloadOutlined,
  WifiOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Dropdown,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import AppLayout from "../components/layout/AppLayout";
import BreadcrumbNav from "../components/layout/BreadcrumbNav";
import type { AuditLog, Device, RedisKey, Stats } from "../types";
import { apiClient } from "../utils/api";
import { useWebSocket } from "../utils/useWebSocket";

const defaultStats: Stats = {
  total_devices: 0,
  online_devices: 0,
  offline_devices: 0,
  total_connections: 0,
  active_connections: 0,
  bytes_received: 0,
  bytes_sent: 0,
  uptime: 0,
};

const Dashboard: React.FC = () => {
  const queryClient = useQueryClient();
  const refreshTimerRef = useRef<NodeJS.Timeout>();

  const {
    data: devices = [],
    isLoading: devicesLoading,
  } = useQuery<Device[]>({
    queryKey: ["devices"],
    queryFn: async () => {
      const [tcpDevices, mqttDevices] = await Promise.all([
        apiClient.getTCPDevices(),
        apiClient.getMQTTDevices(),
      ]);
      return [...(tcpDevices || []), ...(mqttDevices || [])];
    },
    refetchInterval: 30000,
  });

  const {
    data: stats = defaultStats,
    isLoading: statsLoading,
  } = useQuery<Stats>({
    queryKey: ["stats"],
    queryFn: () => apiClient.getStats() as Promise<Stats>,
    refetchInterval: 30000,
  });

  const {
    data: auditLogs = [],
  } = useQuery<AuditLog[]>({
    queryKey: ["auditLogs"],
    queryFn: async () => {
      const logsData = (await apiClient.getAuditLogs()) as AuditLog[];
      return logsData.slice(0, 10);
    },
    refetchInterval: 30000,
  });

  const {
    data: redisKeys = [],
  } = useQuery<RedisKey[]>({
    queryKey: ["redisKeys"],
    queryFn: async () => {
      const keysData = (await apiClient.getRedisKeys()) as RedisKey[];
      return keysData.slice(0, 10);
    },
    refetchInterval: 30000,
  });

  const loading = devicesLoading || statsLoading;

  const { connectionStatus } = useWebSocket(undefined, {
    onMessage: (msg) => {
      if (msg.type === "weight_update" || msg.type === "ptl_event" ||
          msg.type === "ptl_confirm" || msg.type === "ptl_button_press" ||
          msg.type === "device_command") {
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ["devices"] });
          queryClient.invalidateQueries({ queryKey: ["stats"] });
        }, 1000);
      }
    },
  });

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ["devices"] });
    queryClient.invalidateQueries({ queryKey: ["stats"] });
    queryClient.invalidateQueries({ queryKey: ["auditLogs"] });
    queryClient.invalidateQueries({ queryKey: ["redisKeys"] });
  };

  const handleDeviceAction = async (
    deviceId: string,
    action: "disconnect" | "reconnect"
  ) => {
    try {
      queryClient.invalidateQueries({ queryKey: ["devices"] });
      queryClient.invalidateQueries({ queryKey: ["stats"] });
    } catch (error) {
      console.error(`Device ${action} error:`, error);
    }
  };

  const getDeviceMenuItems = (device: Device): MenuProps["items"] => [
    {
      key: "disconnect",
      label: "Disconnect",
      icon: <DisconnectOutlined />,
      danger: true,
      disabled: device.status === "offline",
      onClick: () => handleDeviceAction(device.id, "disconnect"),
    },
    {
      key: "reconnect",
      label: "Reconnect",
      icon: <WifiOutlined />,
      disabled: device.status === "online",
      onClick: () => handleDeviceAction(device.id, "reconnect"),
    },
  ];

  const deviceColumns: ColumnsType<Device> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 150,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 100,
      render: (type: string) => {
        const colorMap: Record<string, string> = {
          scale: "blue",
          ptl: "purple",
          inkjet_printer: "orange",
          dataman: "cyan",
        };
        const labelMap: Record<string, string> = {
          scale: "Scale",
          ptl: "PTL",
          inkjet_printer: "Inkjet",
          dataman: "DataMan",
        };
        return <Tag color={colorMap[type] || "default"}>{labelMap[type] || type}</Tag>;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status: string) => (
        <Badge
          status={status === "online" ? "success" : "error"}
          text={status.toUpperCase()}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, record) => (
        <Dropdown
          menu={{ items: getDeviceMenuItems(record) }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  const auditLogColumns: ColumnsType<AuditLog> = [
    {
      title: "Timestamp",
      dataIndex: "timestamp",
      key: "timestamp",
      width: 180,
    },
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      width: 120,
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
    },
  ];

  const quickStats = (
    <Row gutter={[16, 16]}>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Total Devices"
            value={stats.total_devices}
            prefix={<ApiOutlined />}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Online"
            value={stats.online_devices}
            valueStyle={{ color: "#3f8600" }}
            prefix={<WifiOutlined />}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Offline"
            value={stats.offline_devices}
            valueStyle={{ color: "#cf1322" }}
            prefix={<DisconnectOutlined />}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Active Connections"
            value={stats.active_connections}
            prefix={<WifiOutlined />}
          />
        </Card>
      </Col>
    </Row>
  );

  return (
    <AppLayout
      title="Dashboard"
      extraActions={
        <Space>
          <Tooltip title={`Real-time updates: ${connectionStatus}`}>
            <div className="flex items-center gap-4">
              <Badge
                status={
                  connectionStatus === "connected"
                    ? "processing"
                    : connectionStatus === "connecting"
                    ? "warning"
                    : "error"
                }
              />{" "}
              {connectionStatus === "connected"
                ? "Live"
                : connectionStatus === "connecting"
                ? "Connecting..."
                : "Offline"}
            </div>
          </Tooltip>
          <Tooltip title="Refresh data">
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
            >
              Refresh
            </Button>
          </Tooltip>
        </Space>
      }
    >
      <BreadcrumbNav />

      <div>
        <div style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={16}>
              <Card title="System Statistics">{quickStats}</Card>
            </Col>
            <Col xs={24} lg={8}>
              <Card title="Quick Actions" style={{ height: "100%" }}>
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Link to="/devices">
                    <Button type="primary" block icon={<ApiOutlined />}>
                      Manage Devices
                    </Button>
                  </Link>
                  <Link to="/audit-logs">
                    <Button block icon={<ApiOutlined />}>
                      View Audit Logs
                    </Button>
                  </Link>
                  <Link to="/redis">
                    <Button block icon={<ApiOutlined />}>
                      Redis Management
                    </Button>
                  </Link>
                </Space>
              </Card>
            </Col>
          </Row>
        </div>
        {stats.total_devices === 0 && (
          <Alert
            message="No devices connected"
            description="There are currently no devices connected to the system. Please check your device configurations."
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />
        )}
        <Card
          title="Recent Devices"
          extra={<Link to="/devices">View All</Link>}
        >
          <Table
            columns={deviceColumns}
            dataSource={devices.slice(0, 5)}
            rowKey="id"
            loading={loading}
            scroll={{ x: 500 }}
            pagination={false}
            size="small"
          />
        </Card>
      </div>
    </AppLayout>
  );
};

export default Dashboard;
